import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import icSearch from '@iconify/icons-ic/twotone-search';
import { forkJoin, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { CategoryModel } from '../../../models/category.model';
import { StringHelper } from '../../../helpers/string.helper';
import { CategoryService } from '../../../../pages/application/settings/category/category.service';
import { PurchaseActorService } from '../../../services/purchase-actor.service';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { PURCHASE_ACTOR_MAX_CATEGORY_LEVEL, PurchaseActorScopeEnum } from '../../../models/purchase-actor.model';
import {
  PURCHASE_ACTOR_DEFAULT_NODE_LABEL,
  PurchaseActorNodeEditorComponent,
  PurchaseActorPanelEstablishment,
} from './purchase-actor-node-editor.component';

/** Morceau de libellé d'une famille trouvée : `match` = partie surlignée. */
interface FamilyLabelSegment {
  text: string;
  match: boolean;
}

const FAMILY_SEARCH_MIN_LENGTH = 2;
const FAMILY_SEARCH_DEBOUNCE_MS = 300;

/**
 * Composant partagé « Acteurs achats » (PRED-3139) : card « Acteurs par défaut » (en haut, rôles
 * en colonnes comme l'ancien écran) + arbre des familles (gauche, chargé en une fois jusqu'au
 * dernier niveau paramétrable, avec recherche) + édition de la famille sélectionnée (droite).
 * L'édition d'un nœud (résolution, provenance, diff avec l'héritage, enregistrement) est portée
 * par `purchase-actor-node-editor`, instancié pour la card par défaut et pour la famille.
 * Utilisé par l'onglet établissement et l'onglet tenant « Acteurs achat par défaut ».
 */
@Component({
  selector: 'purchase-actors-panel',
  templateUrl: './purchase-actors-panel.component.html',
  styleUrls: ['./purchase-actors-panel.component.scss'],
})
export class PurchaseActorsPanelComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('categoryEditor') private categoryEditor?: PurchaseActorNodeEditorComponent;
  @Input() scope: PurchaseActorScopeEnum = PurchaseActorScopeEnum.TENANT;
  @Input() establishment?: PurchaseActorPanelEstablishment;
  /** Permissions requises (OU) pour surcharger/éditer/retirer une configuration. Absent = pas de garde. */
  @Input() modifyPermissions?: ENUM_PERMISSIONS[];
  /**
   * Deep-link dashboard (incident FAMILY_WITHOUT_ACTOR, PRED-3139/T16) : id de la famille à
   * présélectionner une fois l'arbre chargé (ses familles parentes sont dépliées). Famille absente
   * de l'arbre (supprimée entre-temps, ou au-delà du dernier niveau paramétrable) : aucune sélection.
   */
  @Input() preselectCategoryId?: string;

  readonly defaultNodeLabel = PURCHASE_ACTOR_DEFAULT_NODE_LABEL;

  private subscription = new Subscription();

  // --- Arbre des familles ---
  treeControl: NestedTreeControl<CategoryModel>;
  dataSource: MatTreeNestedDataSource<CategoryModel>;
  loadingTree = false;
  hasChild = (_: number, node: CategoryModel): boolean => this.hasConfigurableChildren(node);
  /** Toutes les familles de l'arbre par id (nœuds réels, enfants compris). */
  private categoriesById = new Map<string, CategoryModel>();
  /** Libellés normalisés (sans accents ni casse) par id — calculés une fois au chargement. */
  private normalizedLabelsById = new Map<string, string>();

  configuredCategoryIds = new Set<string>();

  // --- Recherche de famille (filtre de l'arbre) ---
  readonly icSearch = icSearch;
  readonly familySearchForm = new UntypedFormGroup({ familySearch: new UntypedFormControl('') });
  /** Familles affichées pendant une recherche (trouvées, leurs parentes, leurs sous-familles) ; null = pas de recherche. */
  private searchVisibleIds: Set<string> | null = null;
  /** Libellés découpés pour le surlignage, par id de famille trouvée (même tableau d'un cycle à l'autre). */
  private searchLabelSegments = new Map<string, FamilyLabelSegment[]>();
  searchHasNoResult = false;

  // --- Sélection ---
  selectedCategory: CategoryModel | null = null;

  /** Jeton de requête — protège `loadConfiguredNodes()` contre les réponses tardives d'un appel obsolète. */
  private configuredNodesSeq = 0;

  constructor(
    private readonly categoryService: CategoryService,
    private readonly purchaseActorService: PurchaseActorService,
  ) {}

  ngOnInit(): void {
    if (this.scope === PurchaseActorScopeEnum.ESTABLISHMENT && !this.establishment) {
      // eslint-disable-next-line no-console
      console.warn('purchase-actors-panel : `establishment` est requis quand `scope` = ESTABLISHMENT.');
    }
    this.initTree();
    this.loadConfiguredNodes();
    this.subscription.add(
      this.familySearchForm
        .get('familySearch')
        .valueChanges.pipe(
          debounceTime(FAMILY_SEARCH_DEBOUNCE_MS),
          map(value => StringHelper.normalizeForSearch(value)),
          distinctUntilChanged(),
        )
        .subscribe(term => this.applyFamilySearch(term)),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const scopeOrEstablishmentChanged = changes.scope || changes.establishment;
    if (scopeOrEstablishmentChanged && !scopeOrEstablishmentChanged.firstChange) {
      this.selectedCategory = null;
      this.loadConfiguredNodes();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // --- Arbre ---

  private initTree(): void {
    this.loadingTree = true;
    this.treeControl = new NestedTreeControl<CategoryModel>(node => node.children);
    this.dataSource = new MatTreeNestedDataSource<CategoryModel>();
    // Arbre complet jusqu'au dernier niveau paramétrable (≈ 440 familles) : la recherche déplie et
    // filtre sans aller-retour serveur.
    const levels = Array.from({ length: PURCHASE_ACTOR_MAX_CATEGORY_LEVEL + 1 }, (_, level) => level);
    forkJoin(levels.map(level => this.categoryService.searchByLevel(level))).subscribe({
      next: categoriesByLevel => {
        this.dataSource.data = this.buildFamilyTree(categoriesByLevel.flat());
        this.loadingTree = false;
        const preselected = this.preselectCategoryId ? this.categoriesById.get(this.preselectCategoryId) : undefined;
        if (preselected) {
          this.expandAncestors(preselected);
          this.selectCategory(preselected);
        }
      },
      error: () => {
        this.loadingTree = false;
      },
    });
  }

  /** Construit l'arbre depuis `/category/list` (parents et parent = ids) : enfants réels triés par
   * libellé, parents résolus en { _id, label, level } pour le fil d'Ariane. Renvoie les racines. */
  private buildFamilyTree(categories: CategoryModel[]): CategoryModel[] {
    const byLabel = (a: CategoryModel, b: CategoryModel): number => a.label.localeCompare(b.label, 'fr');
    this.categoriesById = new Map(
      categories.map(category => [category._id, { ...category, children: [] } as CategoryModel]),
    );
    this.normalizedLabelsById = new Map(
      categories.map(category => [category._id, StringHelper.normalizeForSearch(category.label)]),
    );
    const roots: CategoryModel[] = [];
    for (const node of this.categoriesById.values()) {
      node.parents = (node.parents ?? [])
        .map(parent => this.categoriesById.get(this.categoryRefId(parent)))
        .filter((ancestor): ancestor is CategoryModel => !!ancestor)
        .sort((a, b) => a.level - b.level)
        .map(ancestor => ({ _id: ancestor._id, label: ancestor.label, level: ancestor.level }) as CategoryModel);
      if (node.level === 0) {
        roots.push(node);
      } else {
        this.categoriesById.get(this.categoryRefId(node.parent))?.children.push(node);
      }
    }
    this.categoriesById.forEach(node => node.children.sort(byLabel));
    return roots.sort(byLabel);
  }

  /** Référence de catégorie reçue en id (`/category/list`) ou en objet. */
  private categoryRefId(ref: CategoryModel | string | null | undefined): string | undefined {
    return typeof ref === 'string' ? ref : ref?._id;
  }

  private expandAncestors(node: CategoryModel): void {
    for (const parent of node.parents ?? []) {
      const ancestor = this.categoriesById.get(parent._id);
      if (ancestor) {
        this.treeControl.expand(ancestor);
      }
    }
  }

  /** Recherche (debounce) : affiche les familles trouvées, leurs parentes (dépliées) et leurs
   * sous-familles (repliées) ; masque le reste. Vidée : replie l'arbre sur la famille sélectionnée. */
  private applyFamilySearch(term: string): void {
    this.searchLabelSegments.clear();
    if (term.length < FAMILY_SEARCH_MIN_LENGTH) {
      const wasSearching = !!this.searchVisibleIds;
      this.searchVisibleIds = null;
      this.searchHasNoResult = false;
      if (wasSearching) {
        this.treeControl.collapseAll();
        if (this.selectedCategory) {
          this.expandAncestors(this.selectedCategory);
        }
      }
      return;
    }
    const visibleIds = new Set<string>();
    const matches = [...this.categoriesById.values()].filter(node =>
      this.normalizedLabelsById.get(node._id)?.includes(term),
    );
    this.treeControl.collapseAll();
    for (const match of matches) {
      this.searchLabelSegments.set(match._id, this.splitLabel(match.label, term));
      this.addSubtreeIds(match, visibleIds);
      for (const parent of match.parents ?? []) {
        visibleIds.add(parent._id);
      }
      this.expandAncestors(match);
    }
    this.searchVisibleIds = visibleIds;
    this.searchHasNoResult = matches.length === 0;
  }

  private addSubtreeIds(node: CategoryModel, ids: Set<string>): void {
    ids.add(node._id);
    node.children?.forEach(child => this.addSubtreeIds(child, ids));
  }

  /** Découpe le libellé autour des occurrences de `term` (déjà normalisé). La correspondance est
   * calculée caractère par caractère : un accent décomposé ne décale pas le surlignage. */
  private splitLabel(label: string, term: string): FamilyLabelSegment[] {
    let normalized = '';
    const originIndex: number[] = [];
    let offset = 0;
    for (const char of label) {
      const piece = StringHelper.normalizeForSearch(char);
      for (let i = 0; i < piece.length; i++) {
        originIndex.push(offset);
      }
      normalized += piece;
      offset += char.length;
    }
    originIndex.push(label.length);

    const segments: FamilyLabelSegment[] = [];
    let cursor = 0;
    let found = normalized.indexOf(term);
    while (found !== -1) {
      const start = originIndex[found];
      const end = originIndex[found + term.length];
      if (start > cursor) {
        segments.push({ text: label.slice(cursor, start), match: false });
      }
      segments.push({ text: label.slice(start, end), match: true });
      cursor = end;
      found = normalized.indexOf(term, found + term.length);
    }
    if (cursor < label.length) {
      segments.push({ text: label.slice(cursor), match: false });
    }
    return segments;
  }

  isNodeHidden(node: CategoryModel): boolean {
    return !!this.searchVisibleIds && !this.searchVisibleIds.has(node._id);
  }

  /** Segments surlignés d'une famille trouvée, null hors résultat. */
  labelSegments(node: CategoryModel): FamilyLabelSegment[] | null {
    return this.searchLabelSegments.get(node._id) ?? null;
  }

  /** Règle unique de l'arbre ET de la modale de propagation : pas de sous-famille paramétrable
   * au-delà de `PURCHASE_ACTOR_MAX_CATEGORY_LEVEL` (niveaux non chargés). */
  private hasConfigurableChildren(node: CategoryModel): boolean {
    return !!node.children?.length && node.level < PURCHASE_ACTOR_MAX_CATEGORY_LEVEL;
  }

  isConfigured(node: CategoryModel): boolean {
    return this.configuredCategoryIds.has(node._id);
  }

  isNodeSelected(node: CategoryModel): boolean {
    return this.selectedCategory?._id === node._id;
  }

  /** Tant que l'abandon d'une saisie en cours n'est pas confirmé, la sélection reste inchangée. */
  selectCategory(node: CategoryModel): void {
    const proceed = (): void => {
      this.selectedCategory = node;
    };
    if (this.categoryEditor) {
      this.categoryEditor.confirmDiscardIfDirty(proceed);
    } else {
      proceed();
    }
  }

  get childrenCount(): number {
    return this.selectedCategory && this.hasConfigurableChildren(this.selectedCategory)
      ? this.selectedCategory.children.length
      : 0;
  }

  /** Les acteurs par défaut sont hérités par toutes les familles : la famille affichée est
   * re-résolue (son héritage, voire sa surcharge si propagation « all », a changé). */
  onDefaultSaved(): void {
    this.loadConfiguredNodes();
    this.categoryEditor?.reload();
  }

  loadConfiguredNodes(): void {
    const req = ++this.configuredNodesSeq;
    const establishmentId = this.scope === PurchaseActorScopeEnum.ESTABLISHMENT ? this.establishment?._id : undefined;
    this.purchaseActorService.configuredNodes(establishmentId).subscribe({
      next: response => {
        if (req !== this.configuredNodesSeq) {
          return;
        }
        this.configuredCategoryIds = new Set(response.data.categoryIds);
      },
      error: () => {
        if (req !== this.configuredNodesSeq) {
          return;
        }
        this.configuredCategoryIds = new Set();
      },
    });
  }
}
